import React, {ReactNode, createContext, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Modal} from '@veneer/core';

import {CustomerApiContextType, ErrorResponse} from './types';
import useApis from './utils';

const CustomerApiContext = createContext<CustomerApiContextType>({} as CustomerApiContextType);

const CustomerApiProvider = ({children}: {children: ReactNode}): JSX.Element => {
    const {t} = useTranslation(['errors']);
    const [error, setError] = useState<ErrorResponse | null>(null);
    const apis = useApis(setError);

    const clearError = useCallback(() => setError(null), []);

    const errorMessage = (
        <Modal
            show
            title={t('errors:internalError', 'Application encountered an internal error')}
            closeButton
            closeOnBlur
            onClose={clearError}
        >
            <p>{error?.message}</p>
            {error?.loggingId && (
                <pre>
                    <code>{t('errors:loggingId', 'Logging ID: {{loggingId}}', {loggingId: error.loggingId})}</code>
                </pre>
            )}
        </Modal>
    );

    return (
        <CustomerApiContext.Provider value={apis}>
            {children}
            {error && errorMessage}
        </CustomerApiContext.Provider>
    );
};

const useCustomerApi = () => React.useContext(CustomerApiContext);

export {CustomerApiContext, useCustomerApi, CustomerApiProvider};
